/**
 * @file SettingsPage.jsx
 * @description Settings page with user auth, preferences, and app information
 */

import React, { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { logIn, logOut } from "../UserAuth";
import { doc, getDoc, setDoc, collection, getDocs, writeBatch } from "firebase/firestore";
import { ttsService } from "../utils/textToSpeech";
import { 
  getThemeFromCookie, 
  saveThemeToCookie, 
  applyThemeToDocument, 
  THEME_OPTIONS,
  saveBibleLanguageToCookie,
  getBibleLanguageFromCookie
} from "../utils/theme_utils";
import { getUTCTimeFromLocalHourAndTimezone } from "../utils/dateUtils";
import selectableTimezones from "../assets/selectable_timezones.json";
import { MaterialHeader } from "./MaterialHeader";
import { BIBLE_LANGUAGES, BIBLE_LANGUAGE_LABELS, SUPPORTED_BIBLE_LANGUAGES } from "../constants";
import { 
  getNotificationSettings, 
  updateNotificationSettings, 
  requestNotificationPermission,
  cancelScheduledNotifications,
  scheduleDailyNotification
} from "../utils/notificationUtils";
import { useViewportHeight } from "../utils/viewportUtils";
import { getInstallInstructions } from "../utils/browserUtils";
import { useTextSize } from "../contexts/TextSizeContext";
import { Icon } from "./Icon";

const getJohn316Text = (language) => {
  const verses = {
    [BIBLE_LANGUAGES.ENGLISH]: "John 3:16 For God so loved the world that He gave His only begotten Son, that everyone who believes into Him would not perish, but would have eternal life.",
    [BIBLE_LANGUAGES.SPANISH]: "Juan 3:16 Porque de tal manera amó Dios al mundo, que ha dado a Su Hijo unigénito, para que todo aquel que en Él cree, no perezca, mas tenga vida eterna.",
    [BIBLE_LANGUAGES.SIMPLIFIED_CHINESE]: "约翰福音 3:16 神爱世人，甚至将祂的独生子赐给他们，叫一切信入祂的，不至灭亡，反得永远的生命。",
    [BIBLE_LANGUAGES.TRADITIONAL_CHINESE]: "約翰福音 3:16 神愛世人，甚至將祂的獨生子賜給他們，叫一切信入祂的，不至滅亡，反得永遠的生命。"
  };
  return verses[language] || verses[BIBLE_LANGUAGES.ENGLISH];
};

const getNotificationPermission = () => {
  if (typeof Notification === 'undefined') {
    return 'unsupported';
  }
  return Notification.permission;
};

export const SettingsPage = () => {
  const authState = useAuthState(auth);
  const [user, loading] = authState || [null, false, null];
  const viewportHeight = useViewportHeight();
  const { textSize, changeTextSize, verseTextSize, changeVerseTextSize, getVerseTextSizes } = useTextSize();
  const [userSettings, setUserSettings] = useState({
    displayName: '',
    timezone: 'America/New_York',
    emailTime: 7,
    bibleLanguage: BIBLE_LANGUAGES.ENGLISH
  });
  const [originalSettings, setOriginalSettings] = useState({
    displayName: '',
    timezone: 'America/New_York',
    emailTime: 7,
    bibleLanguage: BIBLE_LANGUAGES.ENGLISH
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [currentTheme, setCurrentTheme] = useState(THEME_OPTIONS.SYSTEM);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    enabled: false,
    time: 7,
    permission: 'default'
  });
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);
  const [showNotificationHelp, setShowNotificationHelp] = useState(false);
  const [showInstallInstructions, setShowInstallInstructions] = useState(false);

  useEffect(() => {
    if (user) {
      // Small delay to ensure createUserInDatabaseIfDoesNotExist completes first
      setTimeout(() => {
        loadUserSettings();
      }, 100);
    }

    // Load theme from cookie
    const savedTheme = getThemeFromCookie();
    setCurrentTheme(savedTheme);

    // Load notification settings from localStorage
    const savedNotificationSettings = getNotificationSettings();
    setNotificationSettings(savedNotificationSettings);

    // Check if app is already installed
    const checkInstallStatus = () => {
      const isInstalled = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
                         (window.navigator.standalone === true);
      setIsAppInstalled(isInstalled);
    };

    checkInstallStatus();

    // Listen for PWA installation events
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setIsAppInstalled(true);
      setInstallPrompt(null);
      showToastMessage('App installed successfully!');
    }


    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadUserSettings = async (retryCount = 0) => {
    const maxRetries = 3;
    setIsLoadingSettings(true);
    
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 7000)
      );

      const fetchPromise = getDoc(doc(db, "users", user.uid));
      const userDoc = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        const settings = {
          displayName: data.displayName || user.displayName || '',
          timezone: data.timezone || 'America/New_York',
          emailTime: data.emailTime || 7,
          bibleLanguage: data.bibleLanguage || BIBLE_LANGUAGES.ENGLISH
        };
        setUserSettings(settings);
        setOriginalSettings(settings);
        
        // Save Bible language to cookie for immediate access
        saveBibleLanguageToCookie(settings.bibleLanguage);
        
        // If bibleLanguage is not set in Firebase, set it to English
        if (!data.bibleLanguage) {
          try {
            await setDoc(doc(db, "users", user.uid), {
              bibleLanguage: BIBLE_LANGUAGES.ENGLISH
            }, { merge: true });
          } catch (error) {
            console.error("Error setting default Bible language:", error);
          }
        }
      } else {
        // User document doesn't exist, create it with defaults
        const defaultSettings = {
          displayName: user.displayName || '',
          timezone: 'America/New_York',
          emailTime: 7,
          bibleLanguage: BIBLE_LANGUAGES.ENGLISH
        };
        
        await setDoc(doc(db, "users", user.uid), {
          ...defaultSettings,
          email: user.email,
          photoURL: user.photoURL
        }, { merge: true });
        
        setUserSettings(defaultSettings);
        setOriginalSettings(defaultSettings);
        saveBibleLanguageToCookie(defaultSettings.bibleLanguage);
      }
    } catch (error) {
      console.error("Error loading user settings:", error);
      
      if (retryCount < maxRetries) {
        console.log(`Retrying loadUserSettings (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(() => {
          loadUserSettings(retryCount + 1);
        }, 1000 * Math.pow(2, retryCount)); // Exponential backoff
      } else {
        showToastMessage('Failed to load settings. Using defaults.');
        // Use defaults with user info if available
        const fallbackSettings = {
          displayName: user.displayName || '',
          timezone: 'America/New_York',
          emailTime: 7,
          bibleLanguage: BIBLE_LANGUAGES.ENGLISH
        };
        setUserSettings(fallbackSettings);
        setOriginalSettings(fallbackSettings);
      }
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const saveUserSettings = async () => {
    if (!user || isSaving) return;
    
    setIsSaving(true);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 10000)
      );
      
      // Get current user document to check for changes in timezone/emailTime
      const userDoc = await getDoc(doc(db, "users", user.uid));
      const currentData = userDoc.exists() ? userDoc.data() : {};
      
      // Calculate new gmtEmailTime based on current settings
      const newGmtEmailTime = getUTCTimeFromLocalHourAndTimezone(
        userSettings.emailTime,
        userSettings.timezone
      ).toString();
      
      const oldGmtEmailTime = currentData.gmtEmailTime;
      
      // Check if timezone or emailTime changed
      const emailTimeChanged = oldGmtEmailTime && oldGmtEmailTime !== newGmtEmailTime;
      
      // Create a batch for atomic operations
      const batch = writeBatch(db);
      
      // Update user document
      const userDocRef = doc(db, "users", user.uid);
      batch.set(userDocRef, {
        ...userSettings,
        email: user.email,
        photoURL: user.photoURL,
        gmtEmailTime: newGmtEmailTime
      }, { merge: true });
      
      // Update email_times collection if email time slot changed
      if (emailTimeChanged) {
        // Add user to new email time slot
        const newEmailTimesDoc = doc(
          db,
          "email_times",
          newGmtEmailTime,
          "users",
          user.uid
        );
        batch.set(newEmailTimesDoc, {});
        
        // Remove user from old email time slot
        if (oldGmtEmailTime) {
          const oldEmailTimesDoc = doc(
            db,
            "email_times",
            oldGmtEmailTime,
            "users",
            user.uid
          );
          batch.delete(oldEmailTimesDoc);
        }
      }
      
      // Commit the batch with timeout
      const batchPromise = batch.commit();
      await Promise.race([batchPromise, timeoutPromise]);
      
      setOriginalSettings(userSettings);
      
      // Save Bible language to cookie for immediate access
      saveBibleLanguageToCookie(userSettings.bibleLanguage);
      
      showToastMessage('Settings saved successfully');
    } catch (error) {
      console.error("Error saving user settings:", error);
      if (error.message === 'Timeout') {
        showToastMessage('Lost connection. Try again later.');
      } else {
        showToastMessage('Failed to save settings. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const hasSettingsChanged = () => {
    return JSON.stringify(userSettings) !== JSON.stringify(originalSettings);
  };

  const getUserChallenges = async () => {
    if (!user) return [];
    
    try {
      // Try to get cached challenges first
      const cacheKey = `userChallenges_${user.uid}`;
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        const { challenges, timestamp } = JSON.parse(cachedData);
        // Use cache if it's less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return challenges.filter(c => !c.archived); // Only active challenges
        }
      }
      
      // If no valid cache, fetch from Firebase
      const userChallengesRef = collection(db, "users", user.uid, "challenges");
      const userChallengesSnapshot = await getDocs(userChallengesRef);
      
      const challengePromises = userChallengesSnapshot.docs.map(async (userChallengeDoc) => {
        const challengeId = userChallengeDoc.id;
        const userChallengeData = userChallengeDoc.data();
        
        const challengeDocRef = doc(db, "challenges", challengeId);
        const challengeDoc = await getDoc(challengeDocRef);
        
        if (challengeDoc.exists()) {
          return {
            id: challengeId,
            ...challengeDoc.data(),
            ...userChallengeData
          };
        }
        return null;
      });
      
      const challenges = (await Promise.all(challengePromises)).filter(Boolean);
      return challenges.filter(c => !c.archived); // Only active challenges
    } catch (error) {
      console.error('Error getting user challenges:', error);
      return [];
    }
  };

  const handleNotificationToggle = async () => {
    if (!notificationSettings.enabled) {
      // Check if permission was previously denied
      const permission = getNotificationPermission();
      if (permission === 'denied' || permission === 'unsupported') {
        setShowNotificationHelp(true);
        return;
      }

      // User is enabling notifications - request permission
      const requestedPermission = await requestNotificationPermission();
      
      if (requestedPermission === 'granted') {
        const updatedSettings = updateNotificationSettings({ 
          enabled: true, 
          permission: requestedPermission 
        });
        setNotificationSettings(updatedSettings);
        
        // Schedule notifications with current user challenges
        const userChallenges = await getUserChallenges();
        await scheduleDailyNotification(updatedSettings.time, userChallenges);
        
        showToastMessage('Notifications enabled! You\'ll receive daily reminders.');
      } else {
        setShowNotificationHelp(true);
      }
    } else {
      // User is disabling notifications
      await cancelScheduledNotifications();
      const updatedSettings = updateNotificationSettings({ enabled: false });
      setNotificationSettings(updatedSettings);
      showToastMessage('Notifications disabled.');
    }
  };

  const handleNotificationTimeChange = async (newTime) => {
    const updatedSettings = updateNotificationSettings({ time: parseInt(newTime) });
    setNotificationSettings(updatedSettings);
    
    // If notifications are enabled, reschedule with new time
    if (updatedSettings.enabled && updatedSettings.permission === 'granted') {
      const userChallenges = await getUserChallenges();
      await scheduleDailyNotification(updatedSettings.time, userChallenges);
      showToastMessage('Notification time updated.');
    }
  };

  const handleInstallApp = async () => {
    if (!installPrompt) {
      // Show manual installation instructions for browsers that don't support install prompts
      setShowInstallInstructions(true);
      return;
    }

    try {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        showToastMessage('App installation started...');
      } else {
        showToastMessage('App installation was cancelled.');
      }
      
      setInstallPrompt(null);
    } catch (error) {
      console.error('Error installing app:', error);
      showToastMessage('Error installing app. Please try again.');
    }
  };


  const toggleTTS = () => {
    if (ttsService.getStatus().isPlaying) {
      ttsService.stop();
    }
    setTtsEnabled(!ttsEnabled);
  };

  const handleThemeChange = (theme) => {
    setCurrentTheme(theme);
    saveThemeToCookie(theme);
    applyThemeToDocument(theme);
  };


  const handleClose = () => {
    window.location.href = '/';
  };

  if (loading) {
    return (
      <>
        <MaterialHeader 
          variant="close" 
          title="Settings"
          onClose={handleClose}
          hideSettings={true}
        />
        <div className="app-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <md-circular-progress indeterminate></md-circular-progress>
        </div>
      </>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <MaterialHeader 
        variant="close" 
        title="Settings"
        onClose={handleClose}
        hideSettings={true}
      />
      <div className="app-content" style={{ 
        height: `calc(${viewportHeight} - 64px)`, 
        overflow: 'hidden',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div className="container" style={{ 
          maxWidth: '800px',
          width: '100%',
          margin: '0 auto',
          flex: '1',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '16px',
          boxSizing: 'border-box'
        }}>

        {/* Content */}
        <div style={{ flex: '1', overflow: 'auto', minHeight: 0 }}>

        {/* User Authentication Section */}
        <md-elevated-card style={{ padding: '16px', marginBottom: '12px', width: '100%', boxSizing: 'border-box' }}>
          <h2 style={{ 
            fontSize: 'var(--md-sys-typescale-headline-medium-size)',
            fontWeight: 'var(--md-sys-typescale-headline-medium-weight)',
            color: 'var(--md-sys-color-on-surface)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icon name="account" />
            Account
          </h2>

          {!user ? (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <p style={{ 
                color: 'var(--md-sys-color-on-surface-variant)',
                marginBottom: '24px'
              }}>
                Sign in to access your reading challenges and sync your progress across devices.
              </p>
              <button 
                onClick={logIn}
                style={{
                  backgroundColor: 'var(--md-sys-color-primary)',
                  color: 'var(--md-sys-color-on-primary)',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '10px 24px',
                  fontSize: 'var(--app-text-size-body)',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  minHeight: '40px',
                  transition: 'all 0.2s ease'
                }}
              >
                <Icon name="signInGoogle" style={{ fontSize: '18px' }} />
                Sign in with Google
              </button>
            </div>
          ) : (
            <div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px',
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: 'var(--md-sys-color-surface-container-low)',
                borderRadius: '12px'
              }}>
                {user.photoURL && (
                  <img 
                    src={user.photoURL} 
                    alt="Profile" 
                    style={{ 
                      width: '48px', 
                      height: '48px', 
                      borderRadius: '50%' 
                    }} 
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: 'var(--app-text-size-body)',
                    fontWeight: '500',
                    color: 'var(--md-sys-color-on-surface)',
                    marginBottom: '4px'
                  }}>
                    {user.displayName}
                  </div>
                  <div style={{ 
                    fontSize: 'var(--app-text-size-body)',
                    color: 'var(--md-sys-color-on-surface-variant)'
                  }}>
                    {user.email}
                  </div>
                </div>
              </div>

              {/* Settings Account Section Description */}
              <div style={{ 
                padding: '12px 16px',
                backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid var(--md-sys-color-outline-variant)'
              }}>
                <p style={{ 
                  margin: 0,
                  fontSize: 'var(--app-text-size-body)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                  lineHeight: '1.4'
                }}>
                  These settings will be saved to your account and synced across all your devices.
                </p>
              </div>

              {/* User Settings Form */}
              {isLoadingSettings ? (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  padding: '48px 0',
                  flexDirection: 'column',
                  gap: '16px'
                }}>
                  <md-circular-progress indeterminate></md-circular-progress>
                  <div style={{ 
                    fontSize: 'var(--app-text-size-body)', 
                    color: 'var(--md-sys-color-on-surface-variant)' 
                  }}>
                    Loading your settings...
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', maxWidth: '100%' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: 'var(--app-text-size-body)', 
                      fontWeight: '500',
                      color: 'var(--md-sys-color-on-surface)'
                    }}>
                      Display Name
                    </label>
                    <input 
                      type="text"
                      value={userSettings.displayName}
                      onChange={(e) => {
                        setUserSettings({ ...userSettings, displayName: e.target.value });
                      }}
                      {...(isLoadingSettings ? { disabled: true } : {})}
                      style={{ 
                        width: '100%', 
                        maxWidth: '100%', 
                        boxSizing: 'border-box',
                        padding: '12px 16px',
                        border: '1px solid var(--md-sys-color-outline)',
                        borderRadius: '4px',
                        backgroundColor: isLoadingSettings ? 'var(--md-sys-color-surface-container-low)' : 'var(--md-sys-color-surface)',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: 'var(--app-text-size-body)',
                        fontFamily: 'inherit',
                        opacity: isLoadingSettings ? 0.6 : 1
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: 'var(--app-text-size-body)', 
                      fontWeight: '500',
                      color: 'var(--md-sys-color-on-surface)'
                    }}>
                      Time Zone
                    </label>
                    <select 
                      value={userSettings.timezone}
                      onChange={(e) => {
                        setUserSettings({ ...userSettings, timezone: e.target.value });
                      }}
                      {...(isLoadingSettings ? { disabled: true } : {})}
                      style={{ 
                        width: '100%', 
                        maxWidth: '100%', 
                        boxSizing: 'border-box',
                        padding: '12px 16px',
                        border: '1px solid var(--md-sys-color-outline)',
                        borderRadius: '4px',
                        backgroundColor: isLoadingSettings ? 'var(--md-sys-color-surface-container-low)' : 'var(--md-sys-color-surface)',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: 'var(--app-text-size-body)',
                        fontFamily: 'inherit',
                        opacity: isLoadingSettings ? 0.6 : 1
                      }}
                    >
                      {Object.entries(selectableTimezones).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: 'var(--app-text-size-body)', 
                      fontWeight: '500',
                      color: 'var(--md-sys-color-on-surface)'
                    }}>
                      Email Reminder Time
                    </label>
                    <select 
                      value={userSettings.emailTime.toString()}
                      onChange={(e) => {
                        setUserSettings({ ...userSettings, emailTime: parseInt(e.target.value) });
                      }}
                      {...(isLoadingSettings ? { disabled: true } : {})}
                      style={{ 
                        width: '100%', 
                        maxWidth: '100%', 
                        boxSizing: 'border-box',
                        padding: '12px 16px',
                        border: '1px solid var(--md-sys-color-outline)',
                        borderRadius: '4px',
                        backgroundColor: isLoadingSettings ? 'var(--md-sys-color-surface-container-low)' : 'var(--md-sys-color-surface)',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: 'var(--app-text-size-body)',
                        fontFamily: 'inherit',
                        opacity: isLoadingSettings ? 0.6 : 1
                      }}
                    >
                      <option value="0">12:00 AM</option>
                      <option value="1">1:00 AM</option>
                      <option value="2">2:00 AM</option>
                      <option value="3">3:00 AM</option>
                      <option value="4">4:00 AM</option>
                      <option value="5">5:00 AM</option>
                      <option value="6">6:00 AM</option>
                      <option value="7">7:00 AM</option>
                      <option value="8">8:00 AM</option>
                      <option value="9">9:00 AM</option>
                      <option value="10">10:00 AM</option>
                      <option value="11">11:00 AM</option>
                      <option value="12">12:00 PM</option>
                      <option value="13">1:00 PM</option>
                      <option value="14">2:00 PM</option>
                      <option value="15">3:00 PM</option>
                      <option value="16">4:00 PM</option>
                      <option value="17">5:00 PM</option>
                      <option value="18">6:00 PM</option>
                      <option value="19">7:00 PM</option>
                      <option value="20">8:00 PM</option>
                      <option value="21">9:00 PM</option>
                      <option value="22">10:00 PM</option>
                      <option value="23">11:00 PM</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontSize: 'var(--app-text-size-body)', 
                      fontWeight: '500',
                      color: 'var(--md-sys-color-on-surface)'
                    }}>
                      Bible Language
                    </label>
                    <select 
                      value={userSettings.bibleLanguage}
                      onChange={(e) => {
                        setUserSettings({ ...userSettings, bibleLanguage: e.target.value });
                      }}
                      {...(isLoadingSettings ? { disabled: true } : {})}
                      style={{ 
                        width: '100%', 
                        maxWidth: '100%', 
                        boxSizing: 'border-box',
                        padding: '12px 16px',
                        border: '1px solid var(--md-sys-color-outline)',
                        borderRadius: '4px',
                        backgroundColor: isLoadingSettings ? 'var(--md-sys-color-surface-container-low)' : 'var(--md-sys-color-surface)',
                        color: 'var(--md-sys-color-on-surface)',
                        fontSize: 'var(--app-text-size-body)',
                        fontFamily: 'inherit',
                        opacity: isLoadingSettings ? 0.6 : 1
                      }}
                    >
                      {SUPPORTED_BIBLE_LANGUAGES.map((language) => (
                        <option key={language} value={language}>
                          {BIBLE_LANGUAGE_LABELS[language]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Save Button */}
              {!isLoadingSettings && (
                <div style={{ marginTop: '24px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button 
                    onClick={saveUserSettings}
                    {...(!hasSettingsChanged() || isSaving || isLoadingSettings ? { disabled: true } : {})}
                    style={{ 
                      backgroundColor: hasSettingsChanged() && !isSaving && !isLoadingSettings ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-low)',
                      color: hasSettingsChanged() && !isSaving && !isLoadingSettings ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-on-surface-variant)',
                      border: 'none',
                      borderRadius: '20px',
                      padding: '10px 24px',
                      fontSize: 'var(--app-text-size-body)',
                      fontWeight: '500',
                      cursor: hasSettingsChanged() && !isSaving && !isLoadingSettings ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minHeight: '40px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                  {isSaving && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid currentColor',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  )}
                  {!isSaving && (
                    <Icon name="save" style={{ fontSize: '18px' }} />
                  )}
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}

              {!isLoadingSettings && (
                <div style={{ marginTop: '24px' }}>
                  <button 
                    onClick={logOut}
                    style={{
                      backgroundColor: 'transparent',
                      color: 'var(--md-sys-color-on-surface)',
                      border: '1px solid var(--md-sys-color-outline)',
                      borderRadius: '20px',
                      padding: '10px 24px',
                      fontSize: 'var(--app-text-size-body)',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minHeight: '40px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Icon name="logout" style={{ fontSize: '18px' }} />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </md-elevated-card>

        {/* App Preferences */}
        <md-elevated-card style={{ padding: '16px', marginBottom: '12px', width: '100%', boxSizing: 'border-box' }}>
          <h2 style={{ 
            fontSize: 'var(--md-sys-typescale-headline-medium-size)',
            fontWeight: 'var(--md-sys-typescale-headline-medium-weight)',
            color: 'var(--md-sys-color-on-surface)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icon name="tune" />
            Preferences
          </h2>

          {/* App Preferences Section Description */}
          <div style={{ 
                padding: '12px 16px',
                backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                borderRadius: '8px',
                marginBottom: '24px',
                border: '1px solid var(--md-sys-color-outline-variant)'
              }}>
            <p style={{ 
              margin: 0,
              fontSize: 'var(--app-text-size-body)',
              color: 'var(--md-sys-color-on-surface-variant)',
              lineHeight: '1.4'
            }}>
              These settings will only be saved to this device.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Theme Setting */}
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '12px', 
                fontSize: 'var(--app-text-size-body)', 
                fontWeight: '500',
                color: 'var(--md-sys-color-on-surface)'
              }}>
                Theme
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => handleThemeChange(THEME_OPTIONS.LIGHT)}
                  style={{
                    backgroundColor: currentTheme === THEME_OPTIONS.LIGHT ? 'var(--md-sys-color-secondary-container)' : 'var(--md-sys-color-surface-container-low)',
                    color: currentTheme === THEME_OPTIONS.LIGHT ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                    border: currentTheme === THEME_OPTIONS.LIGHT ? '2px solid var(--md-sys-color-secondary)' : '1px solid var(--md-sys-color-outline)',
                    borderRadius: '16px',
                    padding: '8px 16px',
                    fontSize: 'var(--app-text-size-body)',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    minWidth: 0
                  }}
                >
                  <Icon name="lightMode" style={{ fontSize: '18px' }} />
                  Light
                </button>
                <button
                  onClick={() => handleThemeChange(THEME_OPTIONS.DARK)}
                  style={{
                    backgroundColor: currentTheme === THEME_OPTIONS.DARK ? 'var(--md-sys-color-secondary-container)' : 'var(--md-sys-color-surface-container-low)',
                    color: currentTheme === THEME_OPTIONS.DARK ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                    border: currentTheme === THEME_OPTIONS.DARK ? '2px solid var(--md-sys-color-secondary)' : '1px solid var(--md-sys-color-outline)',
                    borderRadius: '16px',
                    padding: '8px 16px',
                    fontSize: 'var(--app-text-size-body)',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    minWidth: 0
                  }}
                >
                  <Icon name="darkMode" style={{ fontSize: '18px' }} />
                  Dark
                </button>
                <button
                  onClick={() => handleThemeChange(THEME_OPTIONS.SYSTEM)}
                  style={{
                    backgroundColor: currentTheme === THEME_OPTIONS.SYSTEM ? 'var(--md-sys-color-secondary-container)' : 'var(--md-sys-color-surface-container-low)',
                    color: currentTheme === THEME_OPTIONS.SYSTEM ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                    border: currentTheme === THEME_OPTIONS.SYSTEM ? '2px solid var(--md-sys-color-secondary)' : '1px solid var(--md-sys-color-outline)',
                    borderRadius: '16px',
                    padding: '8px 16px',
                    fontSize: 'var(--app-text-size-body)',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    minWidth: 0
                  }}
                >
                  <Icon name="systemMode" style={{ fontSize: '18px' }} />
                  System
                </button>
              </div>
            </div>

            {/* App Text Size */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: 'var(--app-text-size-body)',
                fontWeight: '500',
                color: 'var(--md-sys-color-on-surface)',
                marginBottom: '8px'
              }}>
                App Text Size
              </div>
              <div style={{
                fontSize: 'var(--app-text-size-small)',
                color: 'var(--md-sys-color-on-surface-variant)',
                marginBottom: '12px'
              }}>
                Adjust the text size for buttons, labels, and other app elements.
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={() => changeTextSize('small')}
                  style={{
                    backgroundColor: textSize === 'small' ? 'var(--md-sys-color-secondary-container)' : 'var(--md-sys-color-surface-container-low)',
                    color: textSize === 'small' ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                    border: textSize === 'small' ? '2px solid var(--md-sys-color-secondary)' : '1px solid var(--md-sys-color-outline)',
                    borderRadius: '16px',
                    padding: '8px 16px',
                    fontSize: 'var(--app-text-size-body)',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    minWidth: 0
                  }}
                >
                  <Icon name="textSmall" style={{ fontSize: '18px' }} />
                  Small
                </button>
                <button
                  onClick={() => changeTextSize('medium')}
                  style={{
                    backgroundColor: textSize === 'medium' ? 'var(--md-sys-color-secondary-container)' : 'var(--md-sys-color-surface-container-low)',
                    color: textSize === 'medium' ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                    border: textSize === 'medium' ? '2px solid var(--md-sys-color-secondary)' : '1px solid var(--md-sys-color-outline)',
                    borderRadius: '16px',
                    padding: '8px 16px',
                    fontSize: 'var(--app-text-size-body)',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    minWidth: 0
                  }}
                >
                  <Icon name="textMedium" style={{ fontSize: '18px' }} />
                  Medium
                </button>
                <button
                  onClick={() => changeTextSize('large')}
                  style={{
                    backgroundColor: textSize === 'large' ? 'var(--md-sys-color-secondary-container)' : 'var(--md-sys-color-surface-container-low)',
                    color: textSize === 'large' ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                    border: textSize === 'large' ? '2px solid var(--md-sys-color-secondary)' : '1px solid var(--md-sys-color-outline)',
                    borderRadius: '16px',
                    padding: '8px 16px',
                    fontSize: 'var(--app-text-size-body)',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexShrink: 0,
                    minWidth: 0
                  }}
                >
                  <Icon name="textLarge" style={{ fontSize: '18px' }} />
                  Large
                </button>
              </div>

              {/* App Text Size Preview */}
              <div style={{
                marginTop: '16px',
                padding: '16px',
                backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                borderRadius: '8px',
                border: '1px solid var(--md-sys-color-outline-variant)'
              }}>
                <div style={{
                  fontSize: 'var(--app-text-size-small)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Preview
                </div>
                <div style={{
                  fontSize: 'var(--app-base-font-size)',
                  color: 'var(--md-sys-color-on-surface)',
                  lineHeight: '1.6',
                  fontFamily: 'inherit'
                }}>
                  Labels, buttons, and navigation text will appear at this size.
                </div>
              </div>
            </div>

            {/* Verse Text Size */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: 'var(--app-text-size-body)',
                fontWeight: '500',
                color: 'var(--md-sys-color-on-surface)',
                marginBottom: '8px'
              }}>
                Verse Text Size
              </div>
              <div style={{
                fontSize: 'var(--app-text-size-small)',
                color: 'var(--md-sys-color-on-surface-variant)',
                marginBottom: '12px'
              }}>
                Adjust the text size for Bible verses displayed in the reading view.
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', width: '100%', maxWidth: '100%' }}>
                {['small', 'medium', 'large', 'xlarge'].map((size) => (
                  <button
                    key={size}
                    onClick={() => changeVerseTextSize(size)}
                    style={{
                      backgroundColor: verseTextSize === size ? 'var(--md-sys-color-secondary-container)' : 'var(--md-sys-color-surface-container-low)',
                      color: verseTextSize === size ? 'var(--md-sys-color-on-secondary-container)' : 'var(--md-sys-color-on-surface-variant)',
                      border: verseTextSize === size ? '2px solid var(--md-sys-color-secondary)' : '1px solid var(--md-sys-color-outline)',
                      borderRadius: '16px',
                      padding: '8px 16px',
                      fontSize: 'var(--app-text-size-body)',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      flexShrink: 0,
                      minWidth: 0,
                      textTransform: 'capitalize'
                    }}
                  >
                    <Icon
                      name={size === 'small' ? 'textSmall' : size === 'medium' ? 'textMedium' : 'textLarge'}
                      style={{ fontSize: '18px' }}
                    />
                    {size === 'xlarge' ? 'X-Large' : size.charAt(0).toUpperCase() + size.slice(1)}
                  </button>
                ))}
              </div>

              {/* Verse Text Size Preview */}
              <div style={{
                marginTop: '16px',
                padding: '16px',
                backgroundColor: 'var(--md-sys-color-surface-container-lowest)',
                borderRadius: '8px',
                border: '1px solid var(--md-sys-color-outline-variant)'
              }}>
                <div style={{
                  fontSize: 'var(--app-text-size-small)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  Preview
                </div>
                <div style={{
                  fontSize: getVerseTextSizes().body,
                  color: 'var(--md-sys-color-on-surface)',
                  lineHeight: '1.6',
                  fontFamily: 'inherit'
                }}>
                  {(() => {
                    const currentBibleLanguage = user ?
                      userSettings.bibleLanguage :
                      getBibleLanguageFromCookie() || BIBLE_LANGUAGES.ENGLISH;
                    return getJohn316Text(currentBibleLanguage);
                  })()}
                </div>
              </div>
            </div>

            {/* Text-to-Speech */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              maxWidth: '95%'
            }}>
              <div>
                <div style={{ 
                  fontSize: 'var(--app-text-size-body)', 
                  fontWeight: '500',
                  color: 'var(--md-sys-color-on-surface)',
                  marginBottom: '4px'
                }}>
                  Text-to-Speech
                </div>
                <div style={{ 
                  fontSize: 'var(--app-text-size-small)',
                  color: 'var(--md-sys-color-on-surface-variant)'
                }}>
                  Enable audio reading of Bible text
                </div>
              </div>
              <md-switch 
                selected={ttsEnabled}
                onClick={toggleTTS}
              />
            </div>

            {/* PWA Installation */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <div>
                <div style={{ 
                  fontSize: 'var(--app-text-size-body)', 
                  fontWeight: '500',
                  color: 'var(--md-sys-color-on-surface)',
                  marginBottom: '4px'
                }}>
                  Install App
                </div>
                <div style={{ 
                  fontSize: 'var(--app-text-size-small)',
                  color: 'var(--md-sys-color-on-surface-variant)'
                }}>
                  {isAppInstalled 
                    ? 'App is installed on this device' 
                    : 'Add to home screen for quick access'
                  }
                </div>
              </div>
              <button
                onClick={handleInstallApp}
                {...(isAppInstalled ? { disabled: true } : {})}
                style={{
                  backgroundColor: isAppInstalled 
                    ? 'var(--md-sys-color-surface-container-low)' 
                    : 'var(--md-sys-color-primary)',
                  color: isAppInstalled 
                    ? 'var(--md-sys-color-on-surface-variant)' 
                    : 'var(--md-sys-color-on-primary)',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '8px 16px',
                  fontSize: 'var(--app-text-size-body)',
                  fontWeight: '500',
                  cursor: isAppInstalled ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
              >
                <Icon
                  name={isAppInstalled ? "installed" : (installPrompt ? "install" : "installInfo")}
                  style={{ fontSize: '16px' }}
                />
                {isAppInstalled ? 'Installed' : (installPrompt ? 'Install' : 'How to Install')}
              </button>
            </div>

            {/* Push Notifications */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: notificationSettings.enabled ? '16px' : '0'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: 'var(--app-text-size-body)', 
                    fontWeight: '500',
                    color: 'var(--md-sys-color-on-surface)',
                    marginBottom: '4px'
                  }}>
                    Push Notifications
                  </div>
                  <div style={{ 
                    fontSize: 'var(--app-text-size-small)',
                    color: 'var(--md-sys-color-on-surface-variant)'
                  }}>
                    Get reminders for reading challenges
                    {(() => {
                      const permission = getNotificationPermission();
                      if (permission === 'denied') {
                        return (
                          <span style={{ color: 'var(--md-sys-color-error)' }}>
                            {' • Permission denied'}
                          </span>
                        );
                      } else if (permission === 'unsupported') {
                        return (
                          <span style={{ color: 'var(--md-sys-color-error)' }}>
                            {' • Not supported in this browser'}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {(() => {
                    const permission = getNotificationPermission();
                    return (permission === 'denied' || permission === 'unsupported') && (
                      <button
                        onClick={() => setShowNotificationHelp(true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: 'var(--md-sys-color-primary)',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        aria-label="Help with notifications"
                      >
                        <Icon name="help" style={{ fontSize: '18px' }} />
                      </button>
                    );
                  })()}
                  <md-switch 
                    selected={notificationSettings.enabled}
                    onClick={handleNotificationToggle}
                    {...(() => {
                      const permission = getNotificationPermission();
                      return (permission === 'denied' || permission === 'unsupported') ? { disabled: true } : {};
                    })()}
                  />
                </div>
              </div>

              {/* Notification Time Selector - Only show when notifications are enabled */}
              {notificationSettings.enabled && (
                <div style={{
                  paddingLeft: '16px',
                  borderLeft: '2px solid var(--md-sys-color-outline-variant)'
                }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontSize: 'var(--app-text-size-body)', 
                    fontWeight: '500',
                    color: 'var(--md-sys-color-on-surface)'
                  }}>
                    Reminder Time
                  </label>
                  <select 
                    value={notificationSettings.time.toString()}
                    onChange={(e) => handleNotificationTimeChange(e.target.value)}
                    style={{ 
                      width: '100%', 
                      maxWidth: '200px', 
                      boxSizing: 'border-box',
                      padding: '8px 12px',
                      border: '1px solid var(--md-sys-color-outline)',
                      borderRadius: '4px',
                      backgroundColor: 'var(--md-sys-color-surface)',
                      color: 'var(--md-sys-color-on-surface)',
                      fontSize: 'var(--app-text-size-body)',
                      fontFamily: 'inherit'
                    }}
                  >
                    <option value="0">12:00 AM</option>
                    <option value="1">1:00 AM</option>
                    <option value="2">2:00 AM</option>
                    <option value="3">3:00 AM</option>
                    <option value="4">4:00 AM</option>
                    <option value="5">5:00 AM</option>
                    <option value="6">6:00 AM</option>
                    <option value="7">7:00 AM</option>
                    <option value="8">8:00 AM</option>
                    <option value="9">9:00 AM</option>
                    <option value="10">10:00 AM</option>
                    <option value="11">11:00 AM</option>
                    <option value="12">12:00 PM</option>
                    <option value="13">1:00 PM</option>
                    <option value="14">2:00 PM</option>
                    <option value="15">3:00 PM</option>
                    <option value="16">4:00 PM</option>
                    <option value="17">5:00 PM</option>
                    <option value="18">6:00 PM</option>
                    <option value="19">7:00 PM</option>
                    <option value="20">8:00 PM</option>
                    <option value="21">9:00 PM</option>
                    <option value="22">10:00 PM</option>
                    <option value="23">11:00 PM</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </md-elevated-card>

        {/* App Information */}
        <md-elevated-card style={{ padding: '24px', width: '100%', boxSizing: 'border-box' }}>
          <h2 style={{ 
            fontSize: 'var(--md-sys-typescale-headline-medium-size)',
            fontWeight: 'var(--md-sys-typescale-headline-medium-weight)',
            color: 'var(--md-sys-color-on-surface)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icon name="info" />
            About
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <md-list-item href="/about">
              <Icon name="article" slot="start" />
              About this site
            </md-list-item>
            <md-list-item href="mailto:mail@memorize.life">
              <Icon name="email" slot="start" />
              Contact
            </md-list-item>
            <md-list-item href="/privacy-policy">
              <Icon name="privacy" slot="start" />
              Privacy Policy
            </md-list-item>
          </div>
        </md-elevated-card>

        </div>
        </div>
      </div>

      {/* Notification Help Modal */}
      {showNotificationHelp && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'var(--md-sys-color-surface-container-high)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <Icon
                name="notifications"
                style={{ 
                  fontSize: '24px', 
                  color: 'var(--md-sys-color-primary)' 
                }}
              />
              <h3 style={{
                fontSize: 'var(--app-text-size-heading)',
                fontWeight: '500',
                color: 'var(--md-sys-color-on-surface)',
                margin: 0
              }}>
                Enable Notifications
              </h3>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <p style={{
                fontSize: 'var(--app-text-size-body)',
                color: 'var(--md-sys-color-on-surface-variant)',
                lineHeight: '1.5',
                margin: '0 0 16px 0'
              }}>
                {(() => {
                  const permission = getNotificationPermission();
                  if (permission === 'unsupported') {
                    return 'Notifications are not supported in this browser. Please try using a modern browser like Chrome, Firefox, or Safari to receive daily reading reminders.';
                  }
                  return 'Notifications are currently blocked. To enable daily reading reminders, you\'ll need to allow notifications in your browser settings.';
                })()}
              </p>
              
              {getNotificationPermission() !== 'unsupported' && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '500',
                    color: 'var(--md-sys-color-on-surface)',
                    margin: '0 0 8px 0'
                  }}>
                    How to enable notifications:
                  </h4>
                
                <div style={{ marginLeft: '16px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: 'var(--md-sys-color-primary)' }}>Chrome/Edge:</strong>
                    <ol style={{ margin: '4px 0 0 20px', fontSize: 'var(--app-text-size-body)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                      <li>Click the lock icon in the address bar</li>
                      <li>Change "Notifications" from "Block" to "Allow"</li>
                      <li>Refresh this page and try again</li>
                    </ol>
                  </div>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: 'var(--md-sys-color-primary)' }}>Safari (iOS):</strong>
                    <ol style={{ margin: '4px 0 0 20px', fontSize: 'var(--app-text-size-body)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                      <li>Go to Settings → Safari → Website Settings</li>
                      <li>Find memorize.life and tap it</li>
                      <li>Change "Notifications" to "Allow"</li>
                      <li>Return to the app and try again</li>
                    </ol>
                  </div>
                  
                  <div>
                    <strong style={{ color: 'var(--md-sys-color-primary)' }}>Firefox:</strong>
                    <ol style={{ margin: '4px 0 0 20px', fontSize: 'var(--app-text-size-body)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                      <li>Click the shield icon in the address bar</li>
                      <li>Click "Permissions" then find "Notifications"</li>
                      <li>Change to "Allow" and refresh the page</li>
                    </ol>
                  </div>
                </div>
                </div>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNotificationHelp(false)}
                style={{
                  backgroundColor: 'var(--md-sys-color-primary)',
                  color: 'var(--md-sys-color-on-primary)',
                  border: 'none',
                  borderRadius: '20px',
                  padding: '10px 24px',
                  fontSize: 'var(--app-text-size-body)',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Install Instructions Modal */}
      {showInstallInstructions && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'var(--md-sys-color-surface-container-high)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            {(() => {
              const instructions = getInstallInstructions();
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <Icon
                      name={instructions.iconName}
                      style={{ 
                        fontSize: '24px', 
                        color: 'var(--md-sys-color-primary)' 
                      }}
                    />
                    <h3 style={{
                      fontSize: '20px',
                      fontWeight: '500',
                      color: 'var(--md-sys-color-on-surface)',
                      margin: 0
                    }}>
                      {instructions.title}
                    </h3>
                  </div>
                  
                  <div style={{ marginBottom: '20px' }}>
                    <div style={{
                      fontSize: 'var(--app-text-size-small)',
                      color: 'var(--md-sys-color-primary)',
                      fontWeight: '500',
                      marginBottom: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      {instructions.platform}
                    </div>
                    
                    {instructions.description && (
                      <p style={{
                        fontSize: 'var(--app-text-size-body)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        lineHeight: '1.5',
                        margin: '0 0 16px 0'
                      }}>
                        {instructions.description}
                      </p>
                    )}
                    
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '500',
                        color: 'var(--md-sys-color-on-surface)',
                        margin: '0 0 12px 0'
                      }}>
                        Installation Steps:
                      </h4>
                      
                      <ol style={{ 
                        margin: '0', 
                        paddingLeft: '20px',
                        fontSize: 'var(--app-text-size-body)',
                        color: 'var(--md-sys-color-on-surface-variant)',
                        lineHeight: '1.6'
                      }}>
                        {instructions.steps.map((step, index) => (
                          <li key={index} style={{ marginBottom: '8px' }}>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </div>
                    
                    {instructions.note && (
                      <div style={{
                        backgroundColor: 'var(--md-sys-color-primary-container)',
                        color: 'var(--md-sys-color-on-primary-container)',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        fontSize: 'var(--app-text-size-body)',
                        lineHeight: '1.4'
                      }}>
                        <strong>Note:</strong> {instructions.note}
                      </div>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowInstallInstructions(false)}
                      style={{
                        backgroundColor: 'var(--md-sys-color-primary)',
                        color: 'var(--md-sys-color-on-primary)',
                        border: 'none',
                        borderRadius: '20px',
                        padding: '10px 24px',
                        fontSize: 'var(--app-text-size-body)',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Got it
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'var(--md-sys-color-inverse-surface)',
          color: 'var(--md-sys-color-inverse-on-surface)',
          padding: '12px 24px',
          borderRadius: '24px',
          fontSize: 'var(--app-text-size-body)',
          fontWeight: '500',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          maxWidth: '90vw',
          textAlign: 'center'
        }}>
          {toastMessage}
        </div>
      )}
    </>
  );
};
